/**
 * 「操作金鑰」（`X-Api-Token`／Worker 的 `SERVICE_TOKEN`）在前端的**唯一一份**存取。
 *
 * 為什麼要集中一支：寫入類端點（`POST /api/accounts/register`、`/say`、`/pm`、`/join`…）
 * 後端要的是 `x-api-token`，跟 PIN 登入的 session 是**兩把不同的鑰匙**。以前只有
 * `apps/console/src/sg/sgApi.ts` 自己讀這個 key、UI 上沒有任何地方可以寫它，於是
 * 「剛登入就被告知登入過期」——其實缺的是這把金鑰。存取集中在這裡，key 字串只寫一次。
 *
 * 存放位置刻意是 **`sessionStorage`**（不是 localStorage）：金鑰只活在這一個分頁，
 * 關掉分頁就沒了；共用電腦上不會留下可以被下一個人撿走的憑證。
 *
 * 讀寫一律包 try/catch：隱私模式下 `sessionStorage` 會直接拋，不該讓整個畫面炸掉。
 */
/** 跟 `apps/console/src/sg/sgApi.ts` 讀的是同一個 key（那邊只讀不寫）。 */
export const API_TOKEN_STORAGE_KEY = 'chat-rea.api.token';
function storage() {
    try {
        return globalThis.sessionStorage ?? null;
    }
    catch {
        // Safari 隱私模式／被封鎖的第三方儲存：當作「沒有地方存」，不要拋。
        return null;
    }
}
/** 目前這把金鑰（沒有＝空字串）。前後空白會被去掉（貼上時常夾到換行）。 */
export function readApiToken() {
    try {
        return (storage()?.getItem(API_TOKEN_STORAGE_KEY) ?? '').trim();
    }
    catch {
        return '';
    }
}
/** 這把金鑰設了沒？（只回布林，不把值帶出去，避免不小心被 log 或塞進 DOM）。 */
export function hasApiToken() {
    return readApiToken() !== '';
}
/** 寫入；傳空字串＝清除（不要存一個空字串，否則 `hasApiToken` 的語意會飄）。 */
export function writeApiToken(value) {
    const token = value.trim();
    try {
        const store = storage();
        if (store === null)
            return;
        if (token === '')
            store.removeItem(API_TOKEN_STORAGE_KEY);
        else
            store.setItem(API_TOKEN_STORAGE_KEY, token);
    }
    catch {
        // 存不進去＝這個分頁的請求仍然沒帶金鑰；由下一次 401 的訊息告訴使用者。
    }
}
/** 清掉（登出、換人用同一台機器時呼叫）。 */
export function clearApiToken() {
    writeApiToken('');
}
/**
 * 給畫面看的遮蔽字串：**永遠不顯示明文**。
 *
 * - 有值：`••••` + 尾四碼（少於 8 碼時只回 `••••`，否則「尾四碼」本身就是大半把鑰匙）
 * - 沒值：空字串（呼叫端自己說「未設定」）
 */
export function maskApiToken(value) {
    const token = value.trim();
    if (token === '')
        return '';
    if (token.length < 8)
        return '••••';
    return `••••${token.slice(-4)}`;
}
//# sourceMappingURL=api-key.js.map